if (!customElements.get('bundle-picker')) {
  customElements.define(
    'bundle-picker',
    class BundlePicker extends HTMLElement {
      constructor() {
        super();
        this.selections = { 1: [], 2: [] };
      }

      get totalSteps() {
        return parseInt(this.dataset.totalSteps) || 1;
      }

      get bundleName() {
        return this.dataset.bundleName || 'Kit';
      }

      get kitVariantId() {
        return this.dataset.kitVariantId;
      }

      get addToCartBtn() {
        return this.querySelector('.bundle-picker__add-to-cart');
      }

      get summaryEl() {
        return this.querySelector('.bundle-picker__summary');
      }

      get summaryListEl() {
        return this.querySelector('.bundle-picker__summary-list');
      }

      get errorEl() {
        return this.querySelector('.product-form__error-message');
      }

      get cartDrawerEl() {
        return document.querySelector('cart-drawer');
      }

      getMaxForStep(step) {
        return parseInt(this.dataset[`step${step}Max`]) || 1;
      }

      getLabelForStep(step) {
        return this.dataset[`step${step}Label`] || `Step ${step}`;
      }

      connectedCallback() {
        this.querySelectorAll('.bundle-picker__checkbox').forEach((checkbox) => {
          checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
        });
        this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }

      getExtraTitles() {
        const raw = this.dataset.extraTitles;
        if (!raw) return [];
        return raw.split('|').map((t) => t.trim()).filter(Boolean);
      }

      isStepComplete(step) {
        return this.selections[step] && this.selections[step].length >= this.getMaxForStep(step);
      }

      handleCheckboxChange(e) {
        const checkbox = e.currentTarget;
        const step = parseInt(checkbox.dataset.step);
        const title = checkbox.dataset.variantTitle;
        const max = this.getMaxForStep(step);

        if (checkbox.checked) {
          if (this.selections[step].length >= max) {
            checkbox.checked = false;
            return;
          }
          this.selections[step].push({
            title: title,
            optionName: checkbox.dataset.optionName || '',
            sku: checkbox.dataset.sku || '',
          });
        } else {
          const idx = this.selections[step].findIndex((s) => s.title === title);
          if (idx > -1) this.selections[step].splice(idx, 1);
        }

        this.updateBlockedState(step);
        this.updateCounters(step);
        this.updateSummary();
        this.updateAddToCartState();
      }

      updateBlockedState(step) {
        const max = this.getMaxForStep(step);
        const atMax = this.selections[step].length >= max;

        this.querySelectorAll(`.bundle-picker__checkbox[data-step="${step}"]`).forEach((cb) => {
          if (cb.dataset.soldOut === 'true') return;
          const label = this.querySelector(`label[for="${cb.id}"]`);
          if (!cb.checked && atMax) {
            cb.disabled = true;
            if (label) label.classList.add('is-blocked');
          } else if (!atMax) {
            cb.disabled = false;
            if (label) label.classList.remove('is-blocked');
          }
        });
      }

      updateCounters(step) {
        const count = this.selections[step].length;
        const max = this.getMaxForStep(step);
        this.querySelectorAll(`[data-step-counter="${step}"]`).forEach((el) => {
          el.textContent = `${count}/${max}`;
        });
      }

      updateSummary() {
        const allSelections = [...this.selections[1], ...(this.selections[2] || [])];

        if (allSelections.length === 0) {
          this.summaryEl.hidden = true;
          return;
        }

        this.summaryEl.hidden = false;

        let html = '';
        for (let s = 1; s <= this.totalSteps; s++) {
          if (this.selections[s].length === 0) continue;
          const label = this.getLabelForStep(s);
          html += `<li class="bundle-picker__summary-group">`;
          html += `<span class="bundle-picker__summary-label">${label}:</span>`;
          html += `<div class="bundle-picker__summary-tags">`;
          this.selections[s].forEach((item) => {
            html += `
              <span class="bundle-picker__tag">
                <span class="bundle-picker__tag-text">${item.title}</span>
                <button type="button" class="bundle-picker__tag-remove" data-step="${s}" data-title="${item.title}" aria-label="Remove ${item.title}">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M1 9l8-8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </span>`;
          });
          html += `</div></li>`;
        }

        this.summaryListEl.innerHTML = html;

        this.summaryListEl.querySelectorAll('.bundle-picker__tag-remove').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            const step = parseInt(e.currentTarget.dataset.step);
            const title = e.currentTarget.dataset.title;
            this.removeSelection(step, title);
          });
        });
      }

      removeSelection(step, title) {
        const idx = this.selections[step].findIndex((s) => s.title === title);
        if (idx > -1) {
          this.selections[step].splice(idx, 1);
          const checkbox = this.querySelector(`.bundle-picker__checkbox[data-variant-title="${title}"]`);
          if (checkbox) checkbox.checked = false;
          this.updateBlockedState(step);
          this.updateCounters(step);
          this.updateSummary();
          this.updateAddToCartState();
        }
      }

      updateAddToCartState() {
        let ready = true;
        for (let s = 1; s <= this.totalSteps; s++) {
          if (this.selections[s].length < this.getMaxForStep(s)) {
            ready = false;
            break;
          }
        }
        this.addToCartBtn.disabled = !ready;
      }

      buildProperties() {
        const properties = { '_is_bundle': 'true' };

        const bundleSku = this.dataset.bundleSku;
        if (bundleSku) {
          properties['_bundle_sku'] = bundleSku;
        }

        for (let step = 1; step <= this.totalSteps; step++) {
          if (!this.selections[step] || this.selections[step].length === 0) continue;

          const label = this.getLabelForStep(step);
          const titles = this.selections[step].map((item) => item.title);
          const skus = this.selections[step].map((item) => item.sku).filter(Boolean);

          properties[label] = titles.join(', ');
          if (skus.length > 0) {
            properties[`${label} SKUs`] = skus.join(', ');
          }
        }

        const extraTitles = this.getExtraTitles();
        if (extraTitles.length > 0) {
          const extraLabel = this.dataset.extraLabel || 'Included';
          properties[extraLabel] = extraTitles.join(', ');
        }

        return properties;
      }

      async handleAddToCart() {
        this.showError();
        this.addToCartBtn.classList.add('btn--loading');
        this.addToCartBtn.disabled = true;

        const properties = this.buildProperties();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const body = JSON.stringify({
          items: [{
            id: parseInt(this.kitVariantId),
            quantity: 1,
            properties: properties,
          }],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(FoxTheme.routes.cart_add_url, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body,
          });
          const parsed = await response.json();

          if (parsed.status) {
            this.showError(parsed.description);
            return;
          }

          if (parsed.status === 422) {
            document.dispatchEvent(
              new CustomEvent('product-ajax:error', {
                detail: { errorMessage: parsed.description },
              })
            );
            return;
          }

          const cartJson = await (await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig())).json();
          cartJson['sections'] = parsed['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: parsed },
            })
          );

          if (this.cartDrawerEl) {
            this.cartDrawerEl.show(this.addToCartBtn);
          }

          this.resetPicker();
        } catch (err) {
          console.error(err);
          this.showError('An error occurred. Please try again.');
        } finally {
          this.addToCartBtn.classList.remove('btn--loading');
          this.updateAddToCartState();
        }
      }

      resetPicker() {
        this.selections = { 1: [], 2: [] };
        this.querySelectorAll('.bundle-picker__checkbox').forEach((cb) => {
          cb.checked = false;
          // Keep sold-out colors disabled when resetting after add-to-cart.
          if (cb.dataset.soldOut !== 'true') cb.disabled = false;
        });
        this.querySelectorAll('label.is-blocked').forEach((l) => l.classList.remove('is-blocked'));
        this.updateCounters(1);
        if (this.totalSteps > 1) this.updateCounters(2);
        this.updateSummary();
        this.updateAddToCartState();
      }

      showError(msg = false) {
        if (this.errorEl) {
          this.errorEl.hidden = !msg;
          if (msg) this.errorEl.textContent = msg;
        }
      }
    }
  );
}
